import { ActionBubble, ChatBubble } from '@/components/shared/ChatBubbles';
import { LoadingState, OverlayLoader } from '@/components/shared/OverlayLoader';
import { SamIcon } from '@/components/shared/SamIcon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RainbowButton } from '@/components/ui/rainbow-button';
import { ScrollArea } from '@/components/ui/scroll-area';
import ShineBorder from '@/components/ui/shine-border';
import {
  ActionExecutionMessage,
  Message,
  ResultMessage,
  Role,
  TextMessage,
} from '@copilotkit/runtime-client-gql';
import { motion } from 'framer-motion';
import { Loader2, Send } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';

export type ChatBotSuggestion = {
  text: string;
  action: () => void;
  variant?: 'rainbow' | 'default' | 'ghost' | 'outline' | 'secondary';
};

export const ChatBot = ({
  visibleMessages,
  isLoading,
  appendMessage,
  inputRef,
  height = '500',
  suggestions,
  disabled,
  overlayLoader,
}: {
  visibleMessages: Message[];
  isLoading: boolean;
  appendMessage: (message: Message) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  height?: string;
  suggestions?: ChatBotSuggestion[];
  disabled?: boolean;
  overlayLoader?: LoadingState;
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  };

  useEffect(() => {
    if (visibleMessages) {
      scrollToBottom();
    }
  }, [visibleMessages]);

  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, inputRef]);

  const { register, handleSubmit: handleSubmitForm, reset } = useForm();

  const handleSubmit = (values: any) => {
    if (disabled || overlayLoader?.isLoading) return;

    appendMessage(
      new TextMessage({
        content: values.message,
        role: Role.User,
      }),
    );

    reset();
  };

  const showLoadingIndicator = isLoading && !overlayLoader?.isLoading;

  return (
    <Card
      className={`p-5 flex flex-1 flex-col overflow-hidden h-[500px] lg:h-[calc(100vh-200px)]`}>
      <ShineBorder
        className={`relative flex flex-1 w-full flex-col items-center justify-center overflow-hidden rounded-lg border bg-background p-0`}>
        <OverlayLoader {...overlayLoader} />
        <ScrollArea
          ref={scrollAreaRef}
          className={`flex-1 relative w-full px-4`}>
          <div className="my-4">
            {visibleMessages.map((message: Message, index: number) => {
              if (message instanceof TextMessage) {
                return <ChatBubble key={index} message={message} />;
              } else if (message instanceof ActionExecutionMessage) {
                return <ActionBubble key={index} message={message} />;
              } else if (message instanceof ResultMessage) {
                return null;
              }

              return null;
            })}

            {suggestions && suggestions.length > 0 && (
              <div className="flex flex-wrap gap-2 my-4">
                {suggestions.map((suggestion, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}>
                    {suggestion.variant === 'rainbow' ? (
                      <RainbowButton
                        onClick={suggestion.action}
                        className="whitespace-nowrap">
                        {suggestion.text}
                      </RainbowButton>
                    ) : (
                      <Button
                        onClick={suggestion.action}
                        variant={suggestion.variant || 'default'}
                        className="whitespace-nowrap">
                        {suggestion.text}
                      </Button>
                    )}
                  </motion.div>
                ))}
              </div>
            )}

            {showLoadingIndicator && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start items-start gap-2">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
                  <SamIcon />
                </div>
                <div className="p-3 rounded-lg mb-2 bg-black text-white border border-gray-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </ShineBorder>
      <form
        onSubmit={handleSubmitForm(handleSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmitForm(handleSubmit)();
          }
        }}>
        <div className="flex mt-4 flex-col gap-2">
          <div className="w-full">
            <div className="flex gap-2">
              <TextareaAutosize
                rows={1}
                {...register('message')}
                placeholder={'Enter your message here...'}
                className="flex-1 resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoading || disabled || overlayLoader?.isLoading}
                maxRows={3}
              />
              <RainbowButton
                type="submit"
                className="h-10 flex-grow-0"
                disabled={disabled || isLoading || overlayLoader?.isLoading}
                isLoading={isLoading && !overlayLoader?.isLoading}
                icon={<Send className="h-4 w-4" />}>
                Send
              </RainbowButton>
            </div>
          </div>
        </div>
      </form>
    </Card>
  );
};
